import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal, Spin, Steps } from "antd";
import { normalizarFocoClinico } from "../../../../Utils/focoClinico";
import { StepIdentity } from "./steps/StepIdentity";
import { StepClinical } from "./steps/StepClinical";
import { StepReview } from "./steps/StepReview";

const STEP_KEYS = {
  identity: ["title", "hubsUrl", "bannerId"],
  clinical: [
    "therapyIds",
    "difficulty",
    "clinicalDescription",
    "clinicalFocus",
    "interventionGuide",
    "contraindications",
    "premium",
    "active",
  ],
};

export function ReviSceneWizardModal({
  open,
  onClose,
  therapyCats,
  onSubmit,
  creating,
  mode = "create",
  initialData,
  loadingInitial = false,
}) {
  const [form] = Form.useForm();
  const [current, setCurrent] = useState(0);
  const [bannerFileList, setBannerFileList] = useState([]);

  const steps = useMemo(
    () => [{ title: "Identidad" }, { title: "Clínico" }, { title: "Revisión" }],
    [],
  );

  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;

    form.resetFields();
    setCurrent(0);
    setBannerFileList([]);

    if (isEdit && initialData?.initialValues) {
      form.setFieldsValue(initialData.initialValues);
      setBannerFileList(initialData.bannerFileList || []);
    }
  }, [form, initialData, isEdit, open]);

  const goNext = async () => {
    try {
      if (current === 0) await form.validateFields(STEP_KEYS.identity);
      if (current === 1) await form.validateFields(STEP_KEYS.clinical);
      setCurrent((v) => Math.min(v + 1, 2));
    } catch {
      // no avanzamos
    }
  };

  const goPrev = () => setCurrent((v) => Math.max(v - 1, 0));

  const handleCancel = () => {
    if (creating) return;
    form.resetFields();
    setBannerFileList([]);
    setCurrent(0);
    onClose();
  };

  const handleFinish = async () => {
    try {
      const values = await form.validateFields();
      const fileToUpload = bannerFileList[0]?.originFileObj || null;

      const payload = {
        identity: {
          idScene: values.idScene,
          title: values.title,
          hubsUrl: values.hubsUrl,
          // EL IDIOMA VIAJA AQUI O NO VIAJA. Esta es la costura que lo perdia:
          // el paso de identidad lo recogia, el controlador lo reenviaba si
          // estaba en `identity`... y este objeto se montaba sin el. Quien
          // subia una sala en frances la veia guardarse sin error y quedar
          // "sin clasificar", que es invisible: la sala se ve igual, hasta que
          // el filtro por idioma existe y entonces "se ve en todos los
          // idiomas" es el sintoma.
          sceneLanguages: values.sceneLanguages,
          sceneLanguageNeutral: values.sceneLanguageNeutral,
        },
        assets: {
          bannerFile: fileToUpload,
        },
        meta: {
          existingBannerId: initialData?.existingBannerId || null,
          // Si la base es de Educa360 (sala compartida), el controlador no
          // sube banner y avisa de que título y URL se aplican en las dos
          // verticales. `undefined` en alta: no aplica.
          isReviBase: isEdit ? initialData?.isReviBase : undefined,
        },
        clinical: {
          therapyIds: values.therapyIds || [],
          difficulty: values.difficulty || "Todos",
          premium: Boolean(values.premium),
          active: values.active ?? true,
          clinicalDescription: values.clinicalDescription || "",
          // Las etiquetas que solo difieren en tildes o mayúsculas se guardan
          // con la grafía del catálogo. Es lo que evita que "relajacion",
          // "Relajación" y "Relajacion" acaben siendo tres cosas distintas en
          // la base de datos, que es exactamente lo que hay hoy. Lo que no
          // gobierna nadie pasa tal cual: se avisa en el paso clínico, no se
          // rechaza.
          clinicalFocus: normalizarFocoClinico(
            values.clinicalFocus,
            therapyCats,
          ),
          interventionGuide: values.interventionGuide || "",
          contraindications: values.contraindications || "",
        },
      };

      await onSubmit(payload);
      handleCancel();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error en wizard submit:", error);
    }
  };

  // ✅ Fallback: si useWatch no devuelve (según versión), usamos getFieldsValue
  const watched = Form.useWatch([], form);
  const summary = watched || form.getFieldsValue(true) || {};

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      title={isEdit ? "Editar sala REVI" : "Crear nueva sala REVI"}
      width={860}
      footer={null}
      destroyOnHidden={false}
      maskClosable={false}
      confirmLoading={creating}
      className="revi-scene-wizard"
    >
      {isEdit && loadingInitial ? (
        <div
          style={{
            minHeight: 280,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Steps current={current} items={steps} style={{ marginBottom: 24 }} />

          <Form form={form} layout="vertical">
            <div style={{ display: current === 0 ? "block" : "none" }}>
              <StepIdentity
                fileList={bannerFileList}
                setFileList={setBannerFileList}
                requiredBanner={!isEdit && !bannerFileList.length}
                compartidaEduca360={isEdit && initialData?.isReviBase === false}
              />
            </div>

            <div style={{ display: current === 1 ? "block" : "none" }}>
              <StepClinical therapyCats={therapyCats} />
            </div>

            {current === 2 ? (
              <StepReview
                summary={summary}
                therapyCats={therapyCats}
                bannerFileList={bannerFileList}
              />
            ) : null}
          </Form>

          <div className="revi-scene-wizard__footer">
            <Button onClick={handleCancel} disabled={creating}>
              Cancelar
            </Button>

            <div className="revi-scene-wizard__footerRight">
              {current > 0 ? (
                <Button onClick={goPrev} disabled={creating}>
                  Atrás
                </Button>
              ) : null}

              {current < 2 ? (
                <Button type="primary" onClick={goNext} disabled={creating}>
                  Siguiente
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={handleFinish}
                  loading={creating}
                >
                  {isEdit ? "Guardar cambios" : "Crear sala"}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
